import React from 'react';

import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { Box, Stack, Typography } from '@mui/material';

import { Button } from '@/components/NewUI/ComponentsLibrary';

import { calendarHref, formatLongDay, formatTime, offsetLabel, zoneCity } from './scheduling';
import { styles } from './styles';
import { aiButtonSx } from '../aiStyles';

import type { IBooking, IBookingLink } from './types';

interface IProps {
  variant: 'confirmed' | 'already_booked';
  companyName: string;
  link: IBookingLink;
  booking: IBooking;
}

// Booked-time summary: the confirmation straight after booking, and the
// read-only view when the candidate opens their link again. The server masks
// the number, including a one-off override.
const BookingSummary = ({ variant, companyName, link, booking }: IProps) => {
  const { slotIso, timeZone } = booking;
  const confirmed = variant === 'confirmed';
  const maskedNumber = booking.maskedPhone;

  const rows: { term: string; value: string; note?: string }[] = [
    {
      term: 'When',
      value: `${formatLongDay(slotIso, timeZone)}, ${formatTime(slotIso, timeZone)}`
    },
    { term: 'Time zone', value: `${zoneCity(timeZone)} time (${offsetLabel(timeZone, slotIso)})` },
    {
      term: 'Rosie will call',
      value: maskedNumber,
      note: booking.differentNumber ? 'For this interview only' : undefined
    },
    {
      term: 'Interview',
      value: `About 10 minutes, ${link.questionCount} question${link.questionCount === 1 ? '' : 's'}`
    }
  ];

  return (
    <Stack sx={styles.narrowColumn}>
      <Stack sx={styles.outcomeHeader}>
        {confirmed ? (
          <CheckCircleIcon aria-hidden sx={styles.successIcon} />
        ) : (
          <EventAvailableIcon aria-hidden sx={styles.bookedIcon} />
        )}
        <Typography component="h1" sx={styles.title}>
          {confirmed ? 'You’re booked in' : 'Your interview is already booked'}
        </Typography>
        <Typography sx={styles.subtitle}>{`${link.jobTitle} · ${companyName}`}</Typography>
      </Stack>

      <Stack aria-labelledby="booking-summary-heading" component="section" sx={styles.card}>
        <Typography component="h2" id="booking-summary-heading" sx={styles.cardHeading}>
          Rosie will call you
        </Typography>
        <Box component="dl" sx={styles.detailList}>
          {rows.map((row) => (
            <Box key={row.term} sx={styles.detailRow}>
              <Typography component="dt" sx={styles.detailTerm}>
                {row.term}
              </Typography>
              <Typography component="dd" sx={styles.detailValue}>
                {row.value}
                {row.note ? (
                  <Typography component="span" sx={styles.detailNote}>
                    {` · ${row.note}`}
                  </Typography>
                ) : null}
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography sx={styles.body}>
          {confirmed
            ? 'We’ve emailed you these details. Keep your phone nearby. If you miss the call, Rosie will try again about 3 hours later, then the next day.'
            : 'You’ve already chosen a time, so this link can’t be used to book again. If you miss the call, Rosie will try again about 3 hours later, then the next day.'}
        </Typography>
        <Stack sx={styles.outcomeActions}>
          <Button
            buttonColor="purple"
            buttonVariant="outline"
            icon={<EventIcon />}
            id="booking-add-to-calendar"
            sx={aiButtonSx}
            href={calendarHref({
              slotIso,
              companyName,
              jobTitle: link.jobTitle,
              maskedPhone: maskedNumber
            })}
          >
            Add to calendar
          </Button>
        </Stack>
      </Stack>

      <Typography sx={styles.contactLine}>
        {`To change this, please contact ${companyName}.`}
      </Typography>
    </Stack>
  );
};

export default BookingSummary;
